import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
// Material Design Icons
import { 
  MdHome, MdSettings, MdNotifications, MdFavorite, 
  MdPerson, MdEmail, MdPhone, MdLock, MdSearch,
  MdCalendarToday, MdBookmark, MdShare, MdDashboard,
  MdList, MdAccountCircle, MdFavoriteBorder
} from 'react-icons/md';
// Font Awesome Icons
import { 
  FaReact, FaGithub, FaTwitter, FaInstagram,
  FaFacebook, FaLinkedin, FaYoutube, FaPinterest,
  FaDiscord, FaTwitch, FaReddit, FaWhatsapp,
  FaSlack, FaTelegram, FaMedium, FaPatreon
} from 'react-icons/fa';
// Heroicons
import { 
  HiUser, HiMail, HiPhone, HiLockClosed,
  HiHome, HiCog, HiBell, HiHeart,
  HiCalendar, HiSearch, HiBookmark, HiShare,
  HiChartBar, HiFolder, HiUserCircle, HiOutlineHeart
} from 'react-icons/hi';
// Bootstrap Icons
import { 
  BiCalendar, BiSearch, BiBookmark, BiShare,
  BiHome, BiCog, BiBell, BiHeart,
  BiUser, BiEnvelope, BiPhone, BiLock,
  BiGridAlt, BiListUl, BiUserCircle, BiHeartCircle
} from 'react-icons/bi';
// Remix Icons
import { 
  RiDashboardLine, RiFileList3Line, RiUserSmileLine, RiHeartLine,
  RiHomeLine, RiSettings3Line, RiNotification3Line, RiHeartsFill,
  RiCalendarLine, RiSearchLine, RiBookmarkLine, RiShareLine,
  RiUser3Line, RiMailLine, RiPhoneLine, RiLockLine
} from 'react-icons/ri';

const IconDemo = () => {
  // Icon gradients for each category
  const iconGradients = {
    material: [
      'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)', // Warm Red
      'linear-gradient(135deg, #4ECDC4 0%, #45B7AF 100%)', // Teal
      'linear-gradient(135deg, #FF61D2 0%, #FE9090 100%)', // Pink
      'linear-gradient(135deg, #2E5FFF 0%, #4FACFE 100%)', // Blue
      'linear-gradient(135deg, #43E97B 0%, #38F9D7 100%)', // Green
      'linear-gradient(135deg, #FA709A 0%, #FEE140 100%)', // Pink to Yellow
      'linear-gradient(135deg, #6B73FF 0%, #000DFF 100%)', // Deep Blue
      'linear-gradient(135deg, #FFA62E 0%, #EA4D2C 100%)', // Orange
      'linear-gradient(135deg, #08AEEA 0%, #2AF598 100%)', // Cyan to Green
      'linear-gradient(135deg, #F6D242 0%, #FF52E5 100%)', // Yellow to Pink
      'linear-gradient(135deg, #17EAD9 0%, #6078EA 100%)', // Cyan to Blue
      'linear-gradient(135deg, #622774 0%, #C53364 100%)', // Purple to Red
      'linear-gradient(135deg, #7117EA 0%, #EA6060 100%)', // Purple to Red
      'linear-gradient(135deg, #42E695 0%, #3BB2B8 100%)', // Green to Teal
      'linear-gradient(135deg, #F02FC2 0%, #6094EA 100%)', // Pink to Blue
      'linear-gradient(135deg, #65FDF0 0%, #1D6FA3 100%)', // Cyan to Blue
    ],
    fontAwesome: [
      'linear-gradient(135deg, #A8E6CF 0%, #3BB2B8 100%)', // Mint to Teal
      'linear-gradient(135deg, #FFD3A5 0%, #FD6585 100%)', // Orange to Pink
      'linear-gradient(135deg, #7F7FD5 0%, #86A8E7 100%)', // Purple to Blue
      'linear-gradient(135deg, #F0C27B 0%, #4B1248 100%)', // Gold to Purple
      'linear-gradient(135deg, #00B4DB 0%, #0083B0 100%)', // Light Blue
      'linear-gradient(135deg, #FEC163 0%, #DE4313 100%)', // Yellow to Orange
      'linear-gradient(135deg, #92FE9D 0%, #00C9FF 100%)', // Green to Blue
      'linear-gradient(135deg, #FF9A9E 0%, #FAD0C4 100%)', // Pink to Peach
      'linear-gradient(135deg, #A8CABA 0%, #5D4157 100%)', // Mint to Purple
      'linear-gradient(135deg, #C2E9FB 0%, #A1C4FD 100%)', // Light Blue
      'linear-gradient(135deg, #FF758C 0%, #FF7EB3 100%)', // Coral to Pink
      'linear-gradient(135deg, #16A085 0%, #F4D03F 100%)', // Green to Yellow
      'linear-gradient(135deg, #4B79A1 0%, #283E51 100%)', // Blue to Navy
      'linear-gradient(135deg, #5F2C82 0%, #49A09D 100%)', // Purple to Teal
      'linear-gradient(135deg, #C33764 0%, #1D2671 100%)', // Red to Blue
      'linear-gradient(135deg, #20BF55 0%, #01BAEF 100%)', // Green to Blue
    ],
    heroicons: [
      'linear-gradient(135deg, #FFD93D 0%, #FF9900 100%)', // Yellow to Orange
      'linear-gradient(135deg, #6C5B7B 0%, #355C7D 100%)', // Purple to Blue
      'linear-gradient(135deg, #C6FFDD 0%, #FBD786 100%)', // Mint to Yellow
      'linear-gradient(135deg, #800080 0%, #FFC0CB 100%)', // Purple to Pink
      'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)', // Teal to Navy
      'linear-gradient(135deg, #F7971E 0%, #FFD200 100%)', // Orange to Yellow
      'linear-gradient(135deg, #8E2DE2 0%, #4A00E0 100%)', // Purple
      'linear-gradient(135deg, #00B09B 0%, #96C93D 100%)', // Teal to Green
      'linear-gradient(135deg, #E44D26 0%, #F16529 100%)', // Red to Orange
      'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)', // Blue to Purple
      'linear-gradient(135deg, #FF512F 0%, #DD2476 100%)', // Orange to Pink
      'linear-gradient(135deg, #16BFFD 0%, #CB3066 100%)', // Blue to Pink
      'linear-gradient(135deg, #1A2980 0%, #26D0CE 100%)', // Blue to Teal
      'linear-gradient(135deg, #FF0099 0%, #493240 100%)', // Pink to Purple
      'linear-gradient(135deg, #004FF9 0%, #FFF94C 100%)', // Blue to Yellow
      'linear-gradient(135deg, #F4C4F3 0%, #FC67FA 100%)', // Light Pink to Pink
    ],
    bootstrap: [
      'linear-gradient(135deg, #6DD5FA 0%, #2980B9 100%)', // Light Blue
      'linear-gradient(135deg, #FF5F6D 0%, #FFC371 100%)', // Red to Yellow
      'linear-gradient(135deg, #11998E 0%, #38EF7D 100%)', // Green
      'linear-gradient(135deg, #FC466B 0%, #3F5EFB 100%)', // Pink to Blue
      'linear-gradient(135deg, #EECDA3 0%, #EF629F 100%)', // Peach to Pink
      'linear-gradient(135deg, #3494E6 0%, #EC6EAD 100%)', // Blue to Pink
      'linear-gradient(135deg, #FD746C 0%, #FF9068 100%)', // Coral
      'linear-gradient(135deg, #6190E8 0%, #A7BFE8 100%)', // Blue
      'linear-gradient(135deg, #44A08D 0%, #093637 100%)', // Teal to Dark
      'linear-gradient(135deg, #5614B0 0%, #DBD65C 100%)', // Purple to Yellow
      'linear-gradient(135deg, #2BC0E4 0%, #EAECC6 100%)', // Blue to Light
      'linear-gradient(135deg, #DA22FF 0%, #9733EE 100%)', // Purple
      'linear-gradient(135deg, #02AAB0 0%, #00CDAC 100%)', // Teal
      'linear-gradient(135deg, #CC2B5E 0%, #753A88 100%)', // Red to Purple
      'linear-gradient(135deg, #EC008C 0%, #FC6767 100%)', // Pink to Red
      'linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)', // Blue to Green
    ],
    remix: [
      'linear-gradient(135deg, #FF9A9E 0%, #FAD0C4 100%)', // Pink to Peach
      'linear-gradient(135deg, #A18CD1 0%, #FBC2EB 100%)', // Purple to Pink
      'linear-gradient(135deg, #96E6A1 0%, #D4FC79 100%)', // Green
      'linear-gradient(135deg, #FDA085 0%, #F6D365 100%)', // Orange to Yellow
      'linear-gradient(135deg, #4481EB 0%, #04BEFE 100%)', // Blue
      'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)', // Pink to Red
      'linear-gradient(135deg, #30CFD0 0%, #330867 100%)', // Teal to Purple
      'linear-gradient(135deg, #43E97B 0%, #38F9D7 100%)', // Green to Cyan
      'linear-gradient(135deg, #FA709A 0%, #FEE140 100%)', // Pink to Yellow
      'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)', // Blue to Purple
      'linear-gradient(135deg, #FF0844 0%, #FFB199 100%)', // Red to Peach
      'linear-gradient(135deg, #C471F5 0%, #FA71CD 100%)', // Purple to Pink
      'linear-gradient(135deg, #7F00FF 0%, #E100FF 100%)', // Purple to Pink
      'linear-gradient(135deg, #0BA360 0%, #3CBA92 100%)', // Green
      'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)', // Yellow to Red
      'linear-gradient(135deg, #F761A1 0%, #8C1BAB 100%)', // Pink to Purple
    ]
  };

  const sectionGradients = {
    material: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)',
    fontAwesome: 'linear-gradient(135deg, #A8E6CF 0%, #3B4371 100%)',
    heroicons: 'linear-gradient(135deg, #FFD93D 0%, #6C5B7B 100%)',
    bootstrap: 'linear-gradient(135deg, #6DD5FA 0%, #2980B9 100%)',
    remix: 'linear-gradient(135deg, #FF9A9E 0%, #FAD0C4 100%)'
  };

  const iconSets = [
    {
      title: 'Material Design Icons',
      icons: [
        { icon: <MdHome size={24} />, name: 'Home' },
        { icon: <MdSettings size={24} />, name: 'Settings' },
        { icon: <MdNotifications size={24} />, name: 'Notifications' },
        { icon: <MdFavorite size={24} />, name: 'Favorite' },
        { icon: <MdPerson size={24} />, name: 'Person' },
        { icon: <MdEmail size={24} />, name: 'Email' },
        { icon: <MdPhone size={24} />, name: 'Phone' },
        { icon: <MdLock size={24} />, name: 'Lock' },
        { icon: <MdSearch size={24} />, name: 'Search' },
        { icon: <MdCalendarToday size={24} />, name: 'Calendar' },
        { icon: <MdBookmark size={24} />, name: 'Bookmark' },
        { icon: <MdShare size={24} />, name: 'Share' },
        { icon: <MdDashboard size={24} />, name: 'Dashboard' },
        { icon: <MdList size={24} />, name: 'List' },
        { icon: <MdAccountCircle size={24} />, name: 'Account' },
        { icon: <MdFavoriteBorder size={24} />, name: 'FavoriteBorder' },
      ],
    },
    {
      title: 'Font Awesome Icons',
      icons: [
        { icon: <FaReact size={24} />, name: 'React' },
        { icon: <FaGithub size={24} />, name: 'GitHub' },
        { icon: <FaTwitter size={24} />, name: 'Twitter' },
        { icon: <FaInstagram size={24} />, name: 'Instagram' },
        { icon: <FaFacebook size={24} />, name: 'Facebook' },
        { icon: <FaLinkedin size={24} />, name: 'LinkedIn' },
        { icon: <FaYoutube size={24} />, name: 'YouTube' },
        { icon: <FaPinterest size={24} />, name: 'Pinterest' },
        { icon: <FaDiscord size={24} />, name: 'Discord' },
        { icon: <FaTwitch size={24} />, name: 'Twitch' },
        { icon: <FaReddit size={24} />, name: 'Reddit' },
        { icon: <FaWhatsapp size={24} />, name: 'WhatsApp' },
        { icon: <FaSlack size={24} />, name: 'Slack' },
        { icon: <FaTelegram size={24} />, name: 'Telegram' },
        { icon: <FaMedium size={24} />, name: 'Medium' },
        { icon: <FaPatreon size={24} />, name: 'Patreon' },
      ],
    },
    {
      title: 'Heroicons',
      icons: [
        { icon: <HiUser size={24} />, name: 'User' },
        { icon: <HiMail size={24} />, name: 'Mail' },
        { icon: <HiPhone size={24} />, name: 'Phone' },
        { icon: <HiLockClosed size={24} />, name: 'Lock' },
        { icon: <HiHome size={24} />, name: 'Home' },
        { icon: <HiCog size={24} />, name: 'Settings' },
        { icon: <HiBell size={24} />, name: 'Bell' },
        { icon: <HiHeart size={24} />, name: 'Heart' },
        { icon: <HiCalendar size={24} />, name: 'Calendar' },
        { icon: <HiSearch size={24} />, name: 'Search' },
        { icon: <HiBookmark size={24} />, name: 'Bookmark' },
        { icon: <HiShare size={24} />, name: 'Share' },
        { icon: <HiChartBar size={24} />, name: 'Chart' },
        { icon: <HiFolder size={24} />, name: 'Folder' },
        { icon: <HiUserCircle size={24} />, name: 'UserCircle' },
        { icon: <HiOutlineHeart size={24} />, name: 'HeartOutline' },
      ],
    },
    {
      title: 'Bootstrap Icons',
      icons: [
        { icon: <BiCalendar size={24} />, name: 'Calendar' },
        { icon: <BiSearch size={24} />, name: 'Search' },
        { icon: <BiBookmark size={24} />, name: 'Bookmark' },
        { icon: <BiShare size={24} />, name: 'Share' },
        { icon: <BiHome size={24} />, name: 'Home' },
        { icon: <BiCog size={24} />, name: 'Settings' },
        { icon: <BiBell size={24} />, name: 'Bell' },
        { icon: <BiHeart size={24} />, name: 'Heart' },
        { icon: <BiUser size={24} />, name: 'User' },
        { icon: <BiEnvelope size={24} />, name: 'Envelope' },
        { icon: <BiPhone size={24} />, name: 'Phone' },
        { icon: <BiLock size={24} />, name: 'Lock' },
        { icon: <BiGridAlt size={24} />, name: 'Grid' },
        { icon: <BiListUl size={24} />, name: 'List' },
        { icon: <BiUserCircle size={24} />, name: 'UserCircle' },
        { icon: <BiHeartCircle size={24} />, name: 'HeartCircle' },
      ],
    },
    {
      title: 'Remix Icons',
      icons: [
        { icon: <RiDashboardLine size={24} />, name: 'Dashboard' },
        { icon: <RiFileList3Line size={24} />, name: 'File List' },
        { icon: <RiUserSmileLine size={24} />, name: 'User Smile' },
        { icon: <RiHeartLine size={24} />, name: 'Heart' },
        { icon: <RiHomeLine size={24} />, name: 'Home' },
        { icon: <RiSettings3Line size={24} />, name: 'Settings' },
        { icon: <RiNotification3Line size={24} />, name: 'Notification' },
        { icon: <RiHeartsFill size={24} />, name: 'Hearts Fill' },
        { icon: <RiCalendarLine size={24} />, name: 'Calendar' },
        { icon: <RiSearchLine size={24} />, name: 'Search' },
        { icon: <RiBookmarkLine size={24} />, name: 'Bookmark' },
        { icon: <RiShareLine size={24} />, name: 'Share' },
        { icon: <RiUser3Line size={24} />, name: 'User' },
        { icon: <RiMailLine size={24} />, name: 'Mail' },
        { icon: <RiPhoneLine size={24} />, name: 'Phone' },
        { icon: <RiLockLine size={24} />, name: 'Lock' },
      ],
    },
  ];

  return (
    <Box sx={{ 
      p: 4,
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f6f8fd 0%, #f0f4fa 100%)'
    }}>
      <Typography variant="h4" gutterBottom sx={{ 
        background: 'linear-gradient(135deg, #4C1D95 30%, #3B0764 90%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        mb: 4,
        fontWeight: 700,
        textAlign: 'center',
        fontSize: { xs: '2rem', md: '2.5rem' },
        letterSpacing: '-0.5px'
      }}>
        Icon Library Showcase
      </Typography>

      <Grid container spacing={4}>
        {iconSets.map((set, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: 4,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: Object.values(sectionGradients)[index],
                }
              }}
            >
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  background: Object.values(sectionGradients)[index],
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block'
                }}
              >
                {set.title}
              </Typography>
              <Grid container spacing={2}>
                {set.icons.map((item, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          background: `${Object.values(iconGradients)[index][i]}15`,
                          transform: 'translateY(-2px)',
                          '& .icon-wrapper': {
                            transform: 'scale(1.1)',
                            background: Object.values(iconGradients)[index][i],
                            '& svg': {
                              color: '#fff',
                            }
                          }
                        },
                      }}
                    >
                      <Box
                        className="icon-wrapper"
                        sx={{
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 2,
                          background: `${Object.values(iconGradients)[index][i]}20`,
                          transition: 'all 0.3s ease',
                          '& svg': {
                            transition: 'all 0.3s ease',
                            color: '#64748B',
                          }
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          mt: 1, 
                          textAlign: 'center',
                          color: '#64748B',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default IconDemo; 